import { DOCUMENT } from '@angular/common';
import {
	Directive,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	NgModule,
	OnDestroy,
	Output,
	Renderer2,
} from '@angular/core';

import {
	clientPointToContentPoint,
	getAutoScrollDeltaY,
	isInteractiveTarget,
	isNodeTarget,
	isScrollbarClick,
	isSemanticCardTarget,
	NormalizedRect,
	normalizeRect,
} from './marquee-selection.utils';

export interface MarqueeSelectionEvent {
	contentRect: NormalizedRect;
	additive: boolean;
}

const DEFAULT_EDGE_SIZE = 48;
const DEFAULT_MAX_SCROLL_SPEED = 16;

@Directive({
	standalone: false,
	selector: '[reachMarqueeSelection]',
})
export class MarqueeSelectionDirective implements OnDestroy {
	private readonly elementRef = inject(ElementRef<HTMLElement>);
	private readonly renderer = inject(Renderer2);
	private readonly document = inject(DOCUMENT);

	private startX = 0;
	private startY = 0;
	private startContentX = 0;
	private startContentY = 0;
	private startScrollTop = 0;
	private startScrollLeft = 0;
	private lastPointerX = 0;
	private lastPointerY = 0;
	private shiftKey = false;
	private isDragging = false;
	private overlay: HTMLDivElement | null = null;
	private overlayContainer: HTMLDivElement | null = null;
	private pendingPointerDown = false;
	private interactionListenersAttached = false;
	private activePointerId: number | null = null;
	private capturedPointerId: number | null = null;
	private previousTouchAction = '';
	private autoScrollFrameId: number | null = null;
	private changeEmitFrameId: number | null = null;
	private pendingChangeEvent: MarqueeSelectionEvent | null = null;
	/** Host viewport rect cached for the duration of a drag (stable while scrolling). */
	private cachedHostRect: DOMRect | null = null;

	private readonly onPointerMove = (event: PointerEvent): void => {
		if (!this.isActivePointer(event.pointerId)) {
			return;
		}

		this.lastPointerX = event.clientX;
		this.lastPointerY = event.clientY;

		if (!this.pendingPointerDown && !this.isDragging) {
			return;
		}

		if (!this.isDragging) {
			const dx = event.clientX - this.startX;
			const dy = event.clientY - this.startY;

			if (Math.hypot(dx, dy) < this.reachMarqueeSelectionThreshold) {
				return;
			}

			this.beginDragging(event);
		}

		event.preventDefault();
		this.updateOverlay(event.clientX, event.clientY);
	};

	private readonly onPointerUp = (event: PointerEvent): void => {
		if (!this.isActivePointer(event.pointerId)) {
			return;
		}

		// Emit for both marquee drags and empty-space clicks so consumers can
		// clear selection when the user clicks outside selected items.
		if (this.isDragging || this.pendingPointerDown) {
			if (this.isDragging) {
				event.preventDefault();
			}

			this.reachMarqueeSelection.emit({
				contentRect: this.getContentMarqueeRect(event.clientX, event.clientY),
				additive: this.shiftKey,
			});
		}

		this.cleanup();
	};

	private readonly onPointerCancel = (event: PointerEvent): void => {
		if (!this.isActivePointer(event.pointerId)) {
			return;
		}

		this.cleanup();
	};

	@Input()
	public reachMarqueeSelectionEnabled = true;

	@Input()
	public reachMarqueeSelectionBlockNodeTargets = true;

	@Input()
	public reachMarqueeSelectionThreshold = 5;

	@Input()
	public reachMarqueeSelectionEdgeSize = DEFAULT_EDGE_SIZE;

	@Input()
	public reachMarqueeSelectionMaxScrollSpeed = DEFAULT_MAX_SCROLL_SPEED;

	@Output()
	public readonly reachMarqueeSelection = new EventEmitter<MarqueeSelectionEvent>();

	@Output()
	public readonly reachMarqueeSelectionActive = new EventEmitter<boolean>();

	@Output()
	public readonly reachMarqueeSelectionChange = new EventEmitter<MarqueeSelectionEvent>();

	constructor() {
		this.elementRef.nativeElement.addEventListener('pointerdown', this.onPointerDown, {
			capture: true,
		});
	}

	ngOnDestroy(): void {
		this.elementRef.nativeElement.removeEventListener('pointerdown', this.onPointerDown, {
			capture: true,
		});
		this.cleanup();
	}

	private readonly onPointerDown = (event: PointerEvent): void => {
		if (!this.reachMarqueeSelectionEnabled || event.button !== 0 || !event.isPrimary) {
			return;
		}

		if (isInteractiveTarget(event.target)) {
			return;
		}

		if (this.reachMarqueeSelectionBlockNodeTargets) {
			if (isNodeTarget(event.target)) {
				return;
			}
		} else if (isSemanticCardTarget(event.target)) {
			return;
		}

		const host = this.elementRef.nativeElement;

		if (isScrollbarClick(event, host)) {
			return;
		}

		event.preventDefault();

		this.startX = event.clientX;
		this.startY = event.clientY;
		this.startScrollTop = host.scrollTop;
		this.startScrollLeft = host.scrollLeft;

		const hostRect = host.getBoundingClientRect() as DOMRect;
		this.cachedHostRect = hostRect;

		const startContent = clientPointToContentPoint(
			event.clientX,
			event.clientY,
			host,
			hostRect
		);
		this.startContentX = startContent.x;
		this.startContentY = startContent.y;

		this.lastPointerX = event.clientX;
		this.lastPointerY = event.clientY;
		this.shiftKey = event.shiftKey;
		this.pendingPointerDown = true;
		this.activePointerId = event.pointerId;
		this.previousTouchAction = host.style.touchAction;

		host.setPointerCapture(event.pointerId);
		this.capturedPointerId = event.pointerId;

		this.attachInteractionListeners();
	};

	private beginDragging(event: PointerEvent): void {
		const host = this.elementRef.nativeElement;

		this.isDragging = true;
		event.preventDefault();
		host.setPointerCapture(event.pointerId);
		this.capturedPointerId = event.pointerId;
		document.body.style.userSelect = 'none';
		host.style.touchAction = 'none';
		this.cachedHostRect ??= host.getBoundingClientRect();

		this.createOverlay();
		this.setActive(true);
		this.startAutoScroll();
	}

	private isActivePointer(pointerId: number): boolean {
		return this.activePointerId === pointerId;
	}

	private releasePointerCapture(): void {
		if (this.capturedPointerId === null) {
			return;
		}

		if (this.elementRef.nativeElement.hasPointerCapture(this.capturedPointerId)) {
			this.elementRef.nativeElement.releasePointerCapture(this.capturedPointerId);
		}

		this.capturedPointerId = null;
	}

	private attachInteractionListeners(): void {
		if (this.interactionListenersAttached) {
			return;
		}

		const host = this.elementRef.nativeElement;
		document.addEventListener('pointermove', this.onPointerMove);
		document.addEventListener('pointerup', this.onPointerUp);
		document.addEventListener('pointercancel', this.onPointerCancel);

		document.body.style.userSelect = 'none';
		host.style.touchAction = 'none';

		this.interactionListenersAttached = true;
	}

	private detachInteractionListeners(): void {
		if (!this.interactionListenersAttached) {
			return;
		}

		const host = this.elementRef.nativeElement;
		document.removeEventListener('pointermove', this.onPointerMove);
		document.removeEventListener('pointerup', this.onPointerUp);
		document.removeEventListener('pointercancel', this.onPointerCancel);
		document.body.style.userSelect = '';
		host.style.touchAction = this.previousTouchAction;
		this.interactionListenersAttached = false;
	}

	private startAutoScroll(): void {
		if (this.autoScrollFrameId !== null) {
			return;
		}

		const tick = (): void => {
			if (!this.isDragging) {
				this.stopAutoScroll();
				return;
			}

			const host = this.elementRef.nativeElement;
			const hostRect = this.getHostRect();
			const deltaY = getAutoScrollDeltaY(this.lastPointerY, hostRect, {
				edgeSize: this.reachMarqueeSelectionEdgeSize,
				maxSpeed: this.reachMarqueeSelectionMaxScrollSpeed,
			});

			if (deltaY !== 0) {
				host.scrollTop += deltaY;
				this.updateOverlay(this.lastPointerX, this.lastPointerY);
			}

			this.autoScrollFrameId = requestAnimationFrame(tick);
		};

		this.autoScrollFrameId = requestAnimationFrame(tick);
	}

	private stopAutoScroll(): void {
		if (this.autoScrollFrameId === null) {
			return;
		}

		cancelAnimationFrame(this.autoScrollFrameId);
		this.autoScrollFrameId = null;
	}

	private createOverlay(): void {
		this.overlayContainer = this.renderer.createElement('div');
		this.renderer.setStyle(this.overlayContainer, 'position', 'fixed');
		this.renderer.setStyle(this.overlayContainer, 'overflow', 'hidden');
		this.renderer.setStyle(this.overlayContainer, 'pointer-events', 'none');
		this.renderer.setStyle(this.overlayContainer, 'z-index', '9999');

		this.overlay = this.renderer.createElement('div');
		this.renderer.setStyle(this.overlay, 'position', 'absolute');
		this.renderer.setStyle(this.overlay, 'border', '1px solid var(--color-primary)');
		this.renderer.setStyle(this.overlay, 'background', 'rgba(var(--color-primary-rgb), 0.12)');

		this.renderer.appendChild(this.overlayContainer, this.overlay);
		this.renderer.appendChild(this.document.body, this.overlayContainer);
		this.updateOverlayContainerBounds(this.getHostRect());
		this.updateOverlay(this.startX, this.startY);
	}

	private updateOverlayContainerBounds(hostRect: DOMRect): void {
		if (!this.overlayContainer) {
			return;
		}

		this.renderer.setStyle(this.overlayContainer, 'top', `${hostRect.top}px`);
		this.renderer.setStyle(this.overlayContainer, 'left', `${hostRect.left}px`);
		this.renderer.setStyle(this.overlayContainer, 'width', `${hostRect.width}px`);
		this.renderer.setStyle(this.overlayContainer, 'height', `${hostRect.height}px`);
	}

	private updateOverlay(currentX: number, currentY: number): void {
		if (!this.overlay || !this.overlayContainer) {
			return;
		}

		const hostRect = this.getHostRect();
		const rect = this.getMarqueeRect(currentX, currentY);

		this.renderer.setStyle(this.overlay, 'left', `${rect.left - hostRect.left}px`);
		this.renderer.setStyle(this.overlay, 'top', `${rect.top - hostRect.top}px`);
		this.renderer.setStyle(this.overlay, 'width', `${rect.width}px`);
		this.renderer.setStyle(this.overlay, 'height', `${rect.height}px`);

		this.scheduleSelectionChange({
			contentRect: this.getContentMarqueeRect(currentX, currentY, hostRect),
			additive: this.shiftKey,
		});
	}

	private scheduleSelectionChange(event: MarqueeSelectionEvent): void {
		this.pendingChangeEvent = event;

		if (this.changeEmitFrameId !== null) {
			return;
		}

		this.changeEmitFrameId = requestAnimationFrame(() => {
			this.changeEmitFrameId = null;
			const pending = this.pendingChangeEvent;
			this.pendingChangeEvent = null;

			if (pending) {
				this.reachMarqueeSelectionChange.emit(pending);
			}
		});
	}

	private cancelScheduledSelectionChange(): void {
		if (this.changeEmitFrameId !== null) {
			cancelAnimationFrame(this.changeEmitFrameId);
			this.changeEmitFrameId = null;
		}

		this.pendingChangeEvent = null;
	}

	private getHostRect(): DOMRect {
		if (this.cachedHostRect) {
			return this.cachedHostRect;
		}

		const hostRect = this.elementRef.nativeElement.getBoundingClientRect() as DOMRect;
		this.cachedHostRect = hostRect;
		return hostRect;
	}

	private getMarqueeRect(currentX: number, currentY: number): NormalizedRect {
		const host = this.elementRef.nativeElement;
		const scrollDeltaY = host.scrollTop - this.startScrollTop;
		const scrollDeltaX = host.scrollLeft - this.startScrollLeft;

		return normalizeRect(
			this.startX - scrollDeltaX,
			this.startY - scrollDeltaY,
			currentX,
			currentY
		);
	}

	private getContentMarqueeRect(
		currentX: number,
		currentY: number,
		hostRect: DOMRect = this.getHostRect()
	): NormalizedRect {
		const host = this.elementRef.nativeElement;
		const endContent = clientPointToContentPoint(currentX, currentY, host, hostRect);

		return normalizeRect(this.startContentX, this.startContentY, endContent.x, endContent.y);
	}

	private setActive(active: boolean): void {
		this.reachMarqueeSelectionActive.emit(active);
	}

	private cleanup(): void {
		this.stopAutoScroll();
		this.cancelScheduledSelectionChange();
		this.releasePointerCapture();
		this.detachInteractionListeners();

		if (this.overlayContainer) {
			this.renderer.removeChild(this.document.body, this.overlayContainer);
			this.overlayContainer = null;
			this.overlay = null;
		}

		const wasDragging = this.isDragging;
		this.pendingPointerDown = false;
		this.isDragging = false;
		this.activePointerId = null;
		this.cachedHostRect = null;

		if (wasDragging) {
			this.setActive(false);
		}
	}
}

@NgModule({
	declarations: [MarqueeSelectionDirective],
	exports: [MarqueeSelectionDirective],
})
export class MarqueeSelectionModule {}
